import { InputConfig, PosCtrlCalResults, SensorsConfig } from 'utils/firestore'
import { radpsToRpm, rpmToRadps } from 'utils/common'
import {
  ControlMode,
  EncoderDirection,
  FluxWeakening,
  HallsensorDirection,
  IStartParamDetectResponse,
  IWriteControlConfigRequest,
  IWritePositionControlSettingsRequest,
  IWritePowerConfigResponse,
  InputMode,
  Mtpa,
  PositionResolution,
  PositionType,
  PositionUnits,
  SensorMode
} from 'utils/common-types'
import { DEFAULT_AMPS_STEP, DEFAULT_RPM_STEP, DEFAULT_VOLTS_STEP } from 'utils/throttle'
import { CStore, Op, OpType, StoredTag, TagsToml } from 'cstore/cstore'

// Two-way mapping between cstore tags and motor config fields.
// [0] is motor cfg path; [1] is cstore tag name
const cstoreMapping: [string, string][] = [
  // Throttle
  ['inputConfig.inputMode', 'IP_InputMode'],
  ['controlConfig.polePairCount', 'CP_ElectricalToMechanicalRatio'],
  ['controlConfig.controlMode', 'CP_ControlMode'],
  ['controlConfig.motorMaxSpeed', 'CP_MaxMotorSpeed'], // Note: wrong type! rpm <-> rad/s
  ['controlConfig.speedControlProportionalGain', 'CP_SpeedControlProportionalGain'],
  ['controlConfig.speedControlIntegralGain', 'CP_SpeedControlIntegralGain'],
  ['controlConfig.mtpa', 'CP_Mtpa'],
  ['controlConfig.fluxWeakening', 'CP_FluxWeakening'],
  ['inputConfig.inputLimitLower', 'IP_Lower'],
  ['inputConfig.inputLimitCenter', 'IP_Center'],
  ['inputConfig.inputLimitUpper', 'IP_Upper'],
  ['inputConfig.alwaysOn', 'IP_AlwaysOn'],

  // Throttle - Position Control
  ['positionControlSettings.positionControlProportionalGain', 'PS_PositionControlPropGain'],
  ['positionControlSettings.positionControlIntegralGain', 'PS_PositionControlIntGain'],
  ['positionControlSettings.positionControlDerivGain', 'PS_PositionControlDerGain'],
  ['positionControlSettings.positionControlMaxSpeed', 'PS_PositionControlMaxSpeed'],
  ['positionControlSettings.positionControlDeadband', 'PS_PositionControlDeadband'],
  ['positionControlSettings.positionControlType', 'PS_PositionType'],
  ['positionControlSettings.positionResolution', 'PS_PositionResolution'],
  ['positionControlSettings.positionUnits', 'PS_PositionUnits'],
  ['positionControlSettings.travelDistance', 'PS_TravelDistance'],
  ['positionControlSettings.positionControlMaxAccel', 'PS_PositionControlMaxAccel'],
  ['positionControlSettings.softStopRangePercent', 'PS_SoftStopRangePercent'],
  ['positionControlCalib.minPosition', 'PSS_MinPosition'], // Note: this was never in motor config
  ['positionControlCalib.maxPosition', 'PSS_MaxPosition'], // Note: this was never in motor config

  // Hardware Limits
  ['powerConfig.batteryCurrentLimit', 'PP_BatteryCurrentLimit'],
  ['powerConfig.overtemperatureThreshold', 'PP_OvertemperatureThreshold'],
  ['powerConfig.criticalOvervoltageThreshold', 'PP_CriticalOverVoltageThreshold'],
  ['powerConfig.warningOvervoltageThreshold', 'PP_WarningOverVoltageThreshold'],
  ['powerConfig.warningUndervoltageThreshold', 'PP_WarningUnderVoltageThreshold'],
  ['powerConfig.criticalUndervoltageThreshold', 'PP_CriticalUnderVoltageThreshold'],
  ['powerConfig.criticalMotorCurrentThreshold', 'PP_CriticalMotorCurrentThreshold'],
  ['powerConfig.warningMotorCurrentThreshold', 'PP_WarningMotorCurrentThreshold'],
  ['powerConfig.motorCurrentLimit', 'PP_MotorCurrentLimit'],

  // PD
  ['parameters.resistance', 'MP_Resistance'],
  ['parameters.ld', 'MP_DirectAxisInductance'],
  ['parameters.lq', 'MP_QuadratureAxisInductance'],
  ['parameters.fluxLinkage', 'MP_FluxLinkage'],

  // Sensors
  ['sensorsConfig.sensorMode', 'SP_SensorMode'],
  ['sensorsConfig.accelerationRamp_mkradps2', 'OLP_OpenLoopAccRamp'],
  ['sensorsConfig.encoderCpr', 'SP_EncoderCPR'],
  ['sensorsConfig.encoderOffset', 'SP_EncoderOffset'], // Encoder only
  ['sensorsConfig.encoderDirection', 'SP_EncoderDirection'], // Encoder only
  ['sensorsConfig.resolverOffset', 'SP_ResolverOffset_rad'], // Resolver only
  ['sensorsConfig.resolverDirection', 'SP_ResolverDirection'], // Resolver only
  ['sensorsConfig.hallsensorForwardOffset', 'SP_FwdHallOffset'],
  ['sensorsConfig.hallsensorReverseOffset', 'SP_ReverseHallOffset'],
  ['sensorsConfig.hallsensorDirection', 'SP_HallDirection'],
  ['sensorsConfig.calibrationSpeed_elradps', 'HS_CalibSpeedElradps']
]

export enum EditorTab {
  ParamDetect,
  Throttle,
  Power,
  Sensors,
  SysID,
  AllSettings
}

export type InvalidReason = {
  tab: EditorTab
  reason: unknown // From exception
}

interface HasInvalidReasons {
  invalidReasons: InvalidReason[]
}

// return the motor config dotted path corresponding to the tag name above, or undefined if not found.
const motorConfigPathFor = (tagName: string): string | undefined => {
  for (const [mc, tag] of cstoreMapping) {
    if (tag === tagName) return mc
  }
}

// return the tag name corresponding to the motor config dotted name above, or undefined if not found.
const cstoreFor = (mcName: string): string | undefined => {
  for (const [mc, tag] of cstoreMapping) {
    if (mc === mcName) return tag
  }
}

// returns a motor config dotted path (see cstoreMapping) or undefined if not found.
const mcPathFromStoredTag = (storedTag: StoredTag, allTags: TagsToml): string | undefined => {
  const tagDef = storedTag.getTagDefinition(allTags)
  if (!isDefined(tagDef)) return
  const mcName = motorConfigPathFor(tagDef.name)
  if (!isDefined(mcName)) return
  return mcName
}

export const getParamDetectFromCStore = (
  cstore: CStore
): IStartParamDetectResponse & HasInvalidReasons => {
  let result: IStartParamDetectResponse = {
    resistance: NaN,
    ld: NaN,
    lq: NaN,
    fluxLinkage: NaN
  }
  // Note: the combo of this loop plus the inner loop in motorConfigPathFor
  // makes this O(n^2) complexity but oh well. It's throw-away code. Other
  // functions are like this, below.
  for (let i = 0; i < cstore.storedTags.length; i++) {
    const storedTag = cstore.storedTags[i]
    const mcName = mcPathFromStoredTag(storedTag, cstore.allTags)
    switch (mcName) {
      case 'parameters.resistance':
        result.resistance = storedTag.getData() as number
        break
      case 'parameters.ld':
        result.ld = storedTag.getData() as number
        break
      case 'parameters.lq':
        result.lq = storedTag.getData() as number
        break
      case 'parameters.fluxLinkage':
        result.fluxLinkage = storedTag.getData() as number
        break
    }
  }
  let invalidReasons: InvalidReason[] = []
  const enqueueInvalid = (reason: unknown) =>
    invalidReasons.push({ tab: EditorTab.ParamDetect, reason })
  for (const [k, v] of Object.entries(result)) {
    if (!isFinite(v)) {
      enqueueInvalid(new Error(`${k} was either not found in cstore, or has value ${v} in cstore`))
    }
  }
  return { ...result, invalidReasons }
}

export const getModificationsToParamDetect = (
  storedPD: IStartParamDetectResponse,
  newPD: IStartParamDetectResponse
): Op[] => {
  let modifyOps: Op[] = []
  if (storedPD.resistance !== newPD.resistance) {
    const tagName = cstoreFor('parameters.resistance')!
    modifyOps.push({ type: OpType.UpdateTag, id: tagName, value: newPD.resistance })
  }
  if (storedPD.ld !== newPD.ld) {
    const tagName = cstoreFor('parameters.ld')!
    modifyOps.push({ type: OpType.UpdateTag, id: tagName, value: newPD.ld })
  }
  if (storedPD.lq !== newPD.lq) {
    const tagName = cstoreFor('parameters.lq')!
    modifyOps.push({ type: OpType.UpdateTag, id: tagName, value: newPD.lq })
  }
  if (storedPD.fluxLinkage !== newPD.fluxLinkage) {
    const tagName = cstoreFor('parameters.fluxLinkage')!
    modifyOps.push({ type: OpType.UpdateTag, id: tagName, value: newPD.fluxLinkage })
  }
  return modifyOps
}

export const getInputConfigFromCStore = (cstore: CStore): InputConfig & HasInvalidReasons => {
  let result: InputConfig = {
    inputLimitUpper: NaN,
    inputLimitLower: NaN,
    inputLimitCenter: NaN,
    inputMode: InputMode.NONE,
    alwaysOn: NaN,
    // In principle we could pull step sizes from non-cstore settings if we had
    // a place to store such settings.
    rpmStep: DEFAULT_RPM_STEP,
    ampsStep: DEFAULT_AMPS_STEP,
    voltsStep: DEFAULT_VOLTS_STEP
  }
  let invalidReasons: InvalidReason[] = []
  const enqueueInvalid = (reason: unknown) =>
    invalidReasons.push({ tab: EditorTab.Throttle, reason })
  for (let i = 0; i < cstore.storedTags.length; i++) {
    const storedTag = cstore.storedTags[i]
    const mcName = mcPathFromStoredTag(storedTag, cstore.allTags)
    switch (mcName) {
      case 'inputConfig.inputLimitUpper':
        result.inputLimitUpper = storedTag.getData() as number
        break
      case 'inputConfig.inputLimitLower':
        result.inputLimitLower = storedTag.getData() as number
        break
      case 'inputConfig.inputLimitCenter':
        result.inputLimitCenter = storedTag.getData() as number
        break
      case 'inputConfig.inputMode':
        result.inputMode = storedTag.getData() as InputMode
        if (typeof InputMode[result.inputMode] !== 'string') {
          enqueueInvalid(new Error(`bad InputMode: ${result.inputMode}`))
        }
        break
      case 'inputConfig.alwaysOn':
        result.alwaysOn = storedTag.getData() as number
        break
    }
  }
  for (const [k, v] of Object.entries(result)) {
    if (!isFinite(v) && k !== 'alwaysOn') {
      enqueueInvalid(new Error(`${k} was either not found in cstore, or has value ${v} in cstore`))
    }
  }
  if (result.inputMode === InputMode.NONE) {
    enqueueInvalid(
      new Error(`inputMode was either not found in cstore, or has value InputMode.NONE in cstore`)
    )
  }
  return { ...result, invalidReasons }
}

export const getModificationsToInputConfig = (stored: InputConfig, newCfg: InputConfig): Op[] => {
  let modifyOps: Op[] = []
  if (stored.inputLimitUpper !== newCfg.inputLimitUpper) {
    const tagName = cstoreFor('inputConfig.inputLimitUpper')!
    modifyOps.push({ type: OpType.UpdateTag, id: tagName, value: newCfg.inputLimitUpper })
  }
  if (stored.inputLimitLower !== newCfg.inputLimitLower) {
    const tagName = cstoreFor('inputConfig.inputLimitLower')!
    modifyOps.push({ type: OpType.UpdateTag, id: tagName, value: newCfg.inputLimitLower })
  }
  if (stored.inputLimitCenter !== newCfg.inputLimitCenter) {
    const tagName = cstoreFor('inputConfig.inputLimitCenter')!
    modifyOps.push({ type: OpType.UpdateTag, id: tagName, value: newCfg.inputLimitCenter })
  }
  if (stored.inputMode !== newCfg.inputMode) {
    const tagName = cstoreFor('inputConfig.inputMode')!
    modifyOps.push({ type: OpType.UpdateTag, id: tagName, value: newCfg.inputMode })
  }
  if (stored.alwaysOn !== newCfg.alwaysOn && isFinite(newCfg.alwaysOn)) {
    const tagName = cstoreFor('inputConfig.alwaysOn')!
    modifyOps.push({ type: OpType.UpdateTag, id: tagName, value: newCfg.alwaysOn })
  }
  // In principle we could save modified step sizes into non-cstore settings if
  // we had a place to store such settings.
  return modifyOps
}

const roundFloat = (n: number, toNearest?: number): number => {
  toNearest ??= 1e-10
  return Math.round(n / toNearest) * toNearest
}

export const getControlConfigFromCStore = (
  cstore: CStore
): IWriteControlConfigRequest & HasInvalidReasons => {
  let result: IWriteControlConfigRequest = {
    polePairCount: NaN,
    speedControlProportionalGain: NaN,
    speedControlIntegralGain: NaN,
    motorMaxSpeed: NaN,
    controlMode: ControlMode.VOLTAGE, // Voltage used as an invalid value
    mtpa: Mtpa.UNKNOWN,
    fluxWeakening: FluxWeakening.UNKNOWN,
    deprecatedPositionControlProportionalGain: -1
  }
  let invalidReasons: InvalidReason[] = []
  const enqueueInvalid = (reason: unknown) =>
    invalidReasons.push({ tab: EditorTab.Throttle, reason })
  for (let i = 0; i < cstore.storedTags.length; i++) {
    const storedTag = cstore.storedTags[i]
    const mcName = mcPathFromStoredTag(storedTag, cstore.allTags)
    switch (mcName) {
      case 'controlConfig.polePairCount':
        result.polePairCount = roundFloat(storedTag.getData() as number)
        break
      case 'controlConfig.speedControlProportionalGain':
        result.speedControlProportionalGain = roundFloat(storedTag.getData() as number, 1e-7)
        break
      case 'controlConfig.speedControlIntegralGain':
        result.speedControlIntegralGain = roundFloat(storedTag.getData() as number, 1e-7)
        break
      case 'controlConfig.motorMaxSpeed':
        // Must convert rad/s to RPM
        result.motorMaxSpeed = roundFloat(radpsToRpm(storedTag.getData() as number), 1e-3)
        break
      case 'controlConfig.controlMode':
        result.controlMode = storedTag.getData() as ControlMode
        if (typeof ControlMode[result.controlMode] !== 'string') {
          enqueueInvalid(new Error(`bad ControlMode: ${result.controlMode}`))
        }
        break
      case 'controlConfig.mtpa':
        result.mtpa = storedTag.getData() as Mtpa
        if (typeof Mtpa[result.mtpa] !== 'string') {
          enqueueInvalid(new Error(`bad Mtpa: ${result.mtpa}`))
        }
        break
      case 'controlConfig.fluxWeakening':
        result.fluxWeakening = storedTag.getData() as FluxWeakening
        if (typeof FluxWeakening[result.fluxWeakening] !== 'string') {
          enqueueInvalid(new Error(`bad FluxWeakening: ${result.fluxWeakening}`))
        }
        break
    }
  }
  for (const [k, v] of Object.entries(result)) {
    if (!isFinite(v)) {
      enqueueInvalid(new Error(`${k} was either not found in cstore, or has value ${v} in cstore`))
    }
  }
  if (result.controlMode === ControlMode.VOLTAGE) {
    enqueueInvalid(
      new Error(
        `controlMode was either not found in cstore, or has value ControlMode.VOLTAGE in cstore`
      )
    )
  }
  if (result.mtpa === Mtpa.UNKNOWN) {
    enqueueInvalid(
      new Error(`mtpa was either not found in cstore, or has value Mtpa.UNKNOWN in cstore`)
    )
  }
  if (result.fluxWeakening === FluxWeakening.UNKNOWN) {
    enqueueInvalid(
      new Error(
        `fluxWeakening was either not found in cstore, or has value FluxWeakening.UNKNOWN in cstore`
      )
    )
  }
  return { ...result, invalidReasons }
}

export const getModificationsToControlConfig = (
  stored: IWriteControlConfigRequest,
  newCfg: IWriteControlConfigRequest
): Op[] => {
  let modifyOps: Op[] = []
  if (stored.motorMaxSpeed !== newCfg.motorMaxSpeed) {
    const tagName = cstoreFor('controlConfig.motorMaxSpeed')!
    // Must convert RPM to rad/s
    modifyOps.push({ type: OpType.UpdateTag, id: tagName, value: rpmToRadps(newCfg.motorMaxSpeed) })
  }
  if (stored.polePairCount !== newCfg.polePairCount) {
    const tagName = cstoreFor('controlConfig.polePairCount')!
    modifyOps.push({ type: OpType.UpdateTag, id: tagName, value: newCfg.polePairCount })
  }
  if (stored.speedControlProportionalGain !== newCfg.speedControlProportionalGain) {
    const tagName = cstoreFor('controlConfig.speedControlProportionalGain')!
    modifyOps.push({
      type: OpType.UpdateTag,
      id: tagName,
      value: newCfg.speedControlProportionalGain
    })
  }
  if (stored.speedControlIntegralGain !== newCfg.speedControlIntegralGain) {
    const tagName = cstoreFor('controlConfig.speedControlIntegralGain')!
    modifyOps.push({ type: OpType.UpdateTag, id: tagName, value: newCfg.speedControlIntegralGain })
  }
  if (stored.controlMode !== newCfg.controlMode) {
    const tagName = cstoreFor('controlConfig.controlMode')!
    modifyOps.push({ type: OpType.UpdateTag, id: tagName, value: newCfg.controlMode })
  }
  if (stored.mtpa !== newCfg.mtpa) {
    const tagName = cstoreFor('controlConfig.mtpa')!
    modifyOps.push({ type: OpType.UpdateTag, id: tagName, value: newCfg.mtpa })
  }
  if (stored.fluxWeakening !== newCfg.fluxWeakening) {
    const tagName = cstoreFor('controlConfig.fluxWeakening')!
    modifyOps.push({ type: OpType.UpdateTag, id: tagName, value: newCfg.fluxWeakening })
  }
  return modifyOps
}

export const getPosCtrlFromCStore = (
  cstore: CStore
): IWritePositionControlSettingsRequest & HasInvalidReasons => {
  let result: IWritePositionControlSettingsRequest = {
    positionControlProportionalGain: NaN,
    positionControlIntegralGain: NaN,
    positionControlDerivGain: NaN,
    positionControlMaxSpeed: NaN,
    positionControlMaxAccel: NaN,
    positionControlDeadband: NaN,
    travelDistance: NaN,
    positionControlType: PositionType.UNKNOWN,
    positionUnits: PositionUnits.UNKNOWN,
    positionResolution: PositionResolution.UNKNOWN,
    softStopRangePercent: 100
  }
  let invalidReasons: InvalidReason[] = []
  const enqueueInvalid = (reason: unknown) =>
    invalidReasons.push({ tab: EditorTab.Throttle, reason })
  for (let i = 0; i < cstore.storedTags.length; i++) {
    const storedTag = cstore.storedTags[i]
    const mcName = mcPathFromStoredTag(storedTag, cstore.allTags)
    switch (mcName) {
      case 'positionControlSettings.positionControlProportionalGain':
        result.positionControlProportionalGain = roundFloat(storedTag.getData() as number, 1e-14)
        break
      case 'positionControlSettings.positionControlIntegralGain':
        result.positionControlIntegralGain = roundFloat(storedTag.getData() as number, 1e-14)
        break
      case 'positionControlSettings.positionControlDerivGain':
        result.positionControlDerivGain = roundFloat(storedTag.getData() as number, 1e-14)
        break
      case 'positionControlSettings.positionControlMaxSpeed':
        result.positionControlMaxSpeed = roundFloat(storedTag.getData() as number, 1e-14)
        break
      case 'positionControlSettings.positionControlMaxAccel':
        result.positionControlMaxAccel = roundFloat(storedTag.getData() as number, 1e-14)
        break
      case 'positionControlSettings.positionControlDeadband':
        result.positionControlDeadband = roundFloat(storedTag.getData() as number, 1e-14)
        break
      case 'positionControlSettings.travelDistance':
        result.travelDistance = roundFloat(storedTag.getData() as number)
        break
      case 'positionControlSettings.positionControlType':
        result.positionControlType = storedTag.getData() as PositionType
        if (typeof PositionType[result.positionControlType] !== 'string') {
          enqueueInvalid(new Error(`bad PositionType: ${result.positionControlType}`))
        }
        break
      case 'positionControlSettings.positionUnits':
        result.positionUnits = storedTag.getData() as PositionUnits
        if (typeof PositionUnits[result.positionUnits] !== 'string') {
          enqueueInvalid(new Error(`bad PositionUnits: ${result.positionUnits}`))
        }
        break
      case 'positionControlSettings.positionResolution':
        result.positionResolution = storedTag.getData() as PositionResolution
        if (typeof PositionResolution[result.positionResolution] !== 'string') {
          enqueueInvalid(new Error(`bad PositionResolution: ${result.positionResolution}`))
        }
        break
      case 'positionControlSettings.softStopRangePercent': {
        // Optional tag — older cstores won't have it. Default 100 (full range).
        const raw = storedTag.getData() as number
        result.softStopRangePercent = Math.max(0, Math.min(100, Math.round(raw)))
        break
      }
    }
  }
  for (const [k, v] of Object.entries(result)) {
    if (!isFinite(v)) {
      enqueueInvalid(new Error(`${k} was either not found in cstore, or has value ${v} in cstore`))
    }
  }
  if (result.positionControlType === PositionType.UNKNOWN) {
    enqueueInvalid(
      new Error(
        `positionControlType was either not found in cstore, or has value PositionType.UNKNOWN in cstore`
      )
    )
  }
  if (result.positionUnits === PositionUnits.UNKNOWN) {
    enqueueInvalid(
      new Error(
        `positionUnits was either not found in cstore, or has value PositionUnits.UNKNOWN in cstore`
      )
    )
  }
  if (result.positionResolution === PositionResolution.UNKNOWN) {
    enqueueInvalid(
      new Error(
        `positionResolution was either not found in cstore, or has value PositionResolution.UNKNOWN in cstore`
      )
    )
  }
  return { ...result, invalidReasons }
}

export const getModificationsToPosCtrl = (
  stored: IWritePositionControlSettingsRequest,
  newCfg: IWritePositionControlSettingsRequest
): Op[] => {
  let modifyOps: Op[] = []
  if (stored.positionControlProportionalGain !== newCfg.positionControlProportionalGain) {
    const tagName = cstoreFor('positionControlSettings.positionControlProportionalGain')!
    modifyOps.push({
      type: OpType.UpdateTag,
      id: tagName,
      value: newCfg.positionControlProportionalGain
    })
  }
  if (stored.positionControlIntegralGain !== newCfg.positionControlIntegralGain) {
    const tagName = cstoreFor('positionControlSettings.positionControlIntegralGain')!
    modifyOps.push({
      type: OpType.UpdateTag,
      id: tagName,
      value: newCfg.positionControlIntegralGain
    })
  }
  if (stored.positionControlDerivGain !== newCfg.positionControlDerivGain) {
    const tagName = cstoreFor('positionControlSettings.positionControlDerivGain')!
    modifyOps.push({ type: OpType.UpdateTag, id: tagName, value: newCfg.positionControlDerivGain })
  }
  if (stored.positionControlMaxSpeed !== newCfg.positionControlMaxSpeed) {
    const tagName = cstoreFor('positionControlSettings.positionControlMaxSpeed')!
    modifyOps.push({ type: OpType.UpdateTag, id: tagName, value: newCfg.positionControlMaxSpeed })
  }
  if (stored.positionControlMaxAccel !== newCfg.positionControlMaxAccel) {
    const tagName = cstoreFor('positionControlSettings.positionControlMaxAccel')!
    modifyOps.push({ type: OpType.UpdateTag, id: tagName, value: newCfg.positionControlMaxAccel })
  }
  if (stored.positionControlDeadband !== newCfg.positionControlDeadband) {
    const tagName = cstoreFor('positionControlSettings.positionControlDeadband')!
    modifyOps.push({ type: OpType.UpdateTag, id: tagName, value: newCfg.positionControlDeadband })
  }
  if (stored.positionControlType !== newCfg.positionControlType) {
    const tagName = cstoreFor('positionControlSettings.positionControlType')!
    modifyOps.push({ type: OpType.UpdateTag, id: tagName, value: newCfg.positionControlType })
  }
  if (stored.positionUnits !== newCfg.positionUnits) {
    const tagName = cstoreFor('positionControlSettings.positionUnits')!
    modifyOps.push({ type: OpType.UpdateTag, id: tagName, value: newCfg.positionUnits })
  }
  if (stored.positionResolution !== newCfg.positionResolution) {
    const tagName = cstoreFor('positionControlSettings.positionResolution')!
    modifyOps.push({ type: OpType.UpdateTag, id: tagName, value: newCfg.positionResolution })
  }
  if (stored.travelDistance !== newCfg.travelDistance) {
    const tagName = cstoreFor('positionControlSettings.travelDistance')!
    modifyOps.push({ type: OpType.UpdateTag, id: tagName, value: newCfg.travelDistance })
  }
  if (stored.softStopRangePercent !== newCfg.softStopRangePercent) {
    const tagName = cstoreFor('positionControlSettings.softStopRangePercent')!
    // UpsertTag (not UpdateTag): the tag was added recently — devices that
    // haven't been reflashed since don't have it in storedTags yet, so a
    // strict update would throw inside cstore.modify() and silently fail the
    // whole export.
    modifyOps.push({ type: OpType.UpsertTag, id: tagName, value: newCfg.softStopRangePercent })
  }
  return modifyOps
}

export const getPowerConfigFromCStore = (
  cstore: CStore
): IWritePowerConfigResponse & HasInvalidReasons => {
  let result: IWritePowerConfigResponse = {
    motorCurrentLimit: NaN,
    batteryCurrentLimit: NaN,
    criticalOvervoltageThreshold: NaN,
    criticalUndervoltageThreshold: NaN,
    overcurrentThresholdDeprecated: 0,
    overtemperatureThreshold: NaN,
    warningOvervoltageThreshold: NaN,
    warningUndervoltageThreshold: NaN,
    warningMotorCurrentThreshold: NaN,
    criticalMotorCurrentThreshold: NaN
  }
  for (let i = 0; i < cstore.storedTags.length; i++) {
    const storedTag = cstore.storedTags[i]
    const mcName = mcPathFromStoredTag(storedTag, cstore.allTags)
    switch (mcName) {
      case 'powerConfig.motorCurrentLimit':
        result.motorCurrentLimit = roundFloat(storedTag.getData() as number, 1e-4)
        break
      case 'powerConfig.batteryCurrentLimit':
        result.batteryCurrentLimit = roundFloat(storedTag.getData() as number, 1e-4)
        break
      case 'powerConfig.criticalOvervoltageThreshold':
        result.criticalOvervoltageThreshold = roundFloat(storedTag.getData() as number, 1e-4)
        break
      case 'powerConfig.criticalUndervoltageThreshold':
        result.criticalUndervoltageThreshold = roundFloat(storedTag.getData() as number, 1e-4)
        break
      case 'powerConfig.overtemperatureThreshold':
        result.overtemperatureThreshold = roundFloat(storedTag.getData() as number, 1e-4)
        break
      case 'powerConfig.warningOvervoltageThreshold':
        result.warningOvervoltageThreshold = roundFloat(storedTag.getData() as number, 1e-4)
        break
      case 'powerConfig.warningUndervoltageThreshold':
        result.warningUndervoltageThreshold = roundFloat(storedTag.getData() as number, 1e-4)
        break
      case 'powerConfig.warningMotorCurrentThreshold':
        result.warningMotorCurrentThreshold = roundFloat(storedTag.getData() as number, 1e-4)
        break
      case 'powerConfig.criticalMotorCurrentThreshold':
        result.criticalMotorCurrentThreshold = roundFloat(storedTag.getData() as number, 1e-4)
        break
    }
  }
  let invalidReasons: InvalidReason[] = []
  const enqueueInvalid = (reason: unknown) => invalidReasons.push({ tab: EditorTab.Power, reason })
  for (const [k, v] of Object.entries(result)) {
    if (!isFinite(v)) {
      enqueueInvalid(new Error(`${k} was either not found in cstore, or has value ${v} in cstore`))
    }
  }
  return { ...result, invalidReasons }
}

export const getModificationsToPowerConfig = (
  stored: IWritePowerConfigResponse,
  newCfg: IWritePowerConfigResponse
): Op[] => {
  let modifyOps: Op[] = []
  if (stored.motorCurrentLimit !== newCfg.motorCurrentLimit) {
    const tagName = cstoreFor('powerConfig.motorCurrentLimit')!
    modifyOps.push({ type: OpType.UpdateTag, id: tagName, value: newCfg.motorCurrentLimit })
  }
  if (stored.batteryCurrentLimit !== newCfg.batteryCurrentLimit) {
    const tagName = cstoreFor('powerConfig.batteryCurrentLimit')!
    modifyOps.push({ type: OpType.UpdateTag, id: tagName, value: newCfg.batteryCurrentLimit })
  }
  if (stored.criticalOvervoltageThreshold !== newCfg.criticalOvervoltageThreshold) {
    const tagName = cstoreFor('powerConfig.criticalOvervoltageThreshold')!
    modifyOps.push({
      type: OpType.UpdateTag,
      id: tagName,
      value: newCfg.criticalOvervoltageThreshold
    })
  }
  if (stored.criticalUndervoltageThreshold !== newCfg.criticalUndervoltageThreshold) {
    const tagName = cstoreFor('powerConfig.criticalUndervoltageThreshold')!
    modifyOps.push({
      type: OpType.UpdateTag,
      id: tagName,
      value: newCfg.criticalUndervoltageThreshold
    })
  }
  if (stored.overtemperatureThreshold !== newCfg.overtemperatureThreshold) {
    const tagName = cstoreFor('powerConfig.overtemperatureThreshold')!
    modifyOps.push({ type: OpType.UpdateTag, id: tagName, value: newCfg.overtemperatureThreshold })
  }
  if (stored.warningOvervoltageThreshold !== newCfg.warningOvervoltageThreshold) {
    const tagName = cstoreFor('powerConfig.warningOvervoltageThreshold')!
    modifyOps.push({
      type: OpType.UpdateTag,
      id: tagName,
      value: newCfg.warningOvervoltageThreshold
    })
  }
  if (stored.warningUndervoltageThreshold !== newCfg.warningUndervoltageThreshold) {
    const tagName = cstoreFor('powerConfig.warningUndervoltageThreshold')!
    modifyOps.push({
      type: OpType.UpdateTag,
      id: tagName,
      value: newCfg.warningUndervoltageThreshold
    })
  }
  if (stored.warningMotorCurrentThreshold !== newCfg.warningMotorCurrentThreshold) {
    const tagName = cstoreFor('powerConfig.warningMotorCurrentThreshold')!
    modifyOps.push({
      type: OpType.UpdateTag,
      id: tagName,
      value: newCfg.warningMotorCurrentThreshold
    })
  }
  if (stored.criticalMotorCurrentThreshold !== newCfg.criticalMotorCurrentThreshold) {
    const tagName = cstoreFor('powerConfig.criticalMotorCurrentThreshold')!
    modifyOps.push({
      type: OpType.UpdateTag,
      id: tagName,
      value: newCfg.criticalMotorCurrentThreshold
    })
  }
  return modifyOps
}

export const getSensorsConfigFromCStore = (cstore: CStore): SensorsConfig & HasInvalidReasons => {
  let result: SensorsConfig = {
    sensorMode: SensorMode.SENSORLESS,
    encoderCpr: NaN,
    accelerationRamp_mkradps2: NaN,
    calibrationSpeed_elradps: 1000.0,
    encoderOffset: NaN,
    encoderDirection: EncoderDirection.UNKNOWN,
    resolverOffset: NaN,
    resolverDirection: EncoderDirection.UNKNOWN,
    hallsensorForwardOffset: NaN,
    hallsensorReverseOffset: NaN,
    hallsensorDirection: HallsensorDirection.UNKNOWN
  }
  let foundSensorMode: boolean = false
  let invalidReasons: InvalidReason[] = []
  const enqueueInvalid = (reason: unknown) =>
    invalidReasons.push({ tab: EditorTab.Sensors, reason })
  for (let i = 0; i < cstore.storedTags.length; i++) {
    const storedTag = cstore.storedTags[i]
    const mcName = mcPathFromStoredTag(storedTag, cstore.allTags)
    switch (mcName) {
      case 'sensorsConfig.encoderCpr':
        result.encoderCpr = storedTag.getData() as number
        break
      case 'sensorsConfig.accelerationRamp_mkradps2':
        result.accelerationRamp_mkradps2 = roundFloat(storedTag.getData() as number)
        break
      case 'sensorsConfig.encoderOffset':
        result.encoderOffset = roundFloat(storedTag.getData() as number)
        break
      case 'sensorsConfig.resolverOffset':
        result.resolverOffset = roundFloat(storedTag.getData() as number)
        break
      case 'sensorsConfig.hallsensorForwardOffset':
        result.hallsensorForwardOffset = roundFloat(storedTag.getData() as number)
        break
      case 'sensorsConfig.hallsensorReverseOffset':
        result.hallsensorReverseOffset = roundFloat(storedTag.getData() as number)
        break
      case 'sensorsConfig.sensorMode':
        result.sensorMode = storedTag.getData() as SensorMode
        if (typeof SensorMode[result.sensorMode] !== 'string') {
          enqueueInvalid(new Error(`bad SensorMode: ${result.sensorMode}`))
        }
        foundSensorMode = true
        break
      case 'sensorsConfig.encoderDirection':
        result.encoderDirection = storedTag.getData() as EncoderDirection
        if (typeof EncoderDirection[result.encoderDirection] !== 'string') {
          enqueueInvalid(new Error(`bad EncoderDirection: ${result.encoderDirection}`))
        }
        break
      case 'sensorsConfig.resolverDirection':
        result.resolverDirection = storedTag.getData() as EncoderDirection
        if (typeof EncoderDirection[result.resolverDirection] !== 'string') {
          enqueueInvalid(new Error(`bad ResolverDirection: ${result.resolverDirection}`))
        }
        break
      case 'sensorsConfig.hallsensorDirection':
        result.hallsensorDirection = storedTag.getData() as HallsensorDirection
        if (typeof HallsensorDirection[result.hallsensorDirection] !== 'string') {
          enqueueInvalid(new Error(`bad HallsensorDirection: ${result.hallsensorDirection}`))
        }
        break
      case 'sensorsConfig.calibrationSpeed_elradps':
        if (isFinite(roundFloat(storedTag.getData() as number))) {
          result.calibrationSpeed_elradps = roundFloat(storedTag.getData() as number)
        }

        break
    }
  }
  for (const [k, v] of Object.entries(result)) {
    // Special case -- allow calibration-derived offsets to be NaN/absent in cstore.
    // These are non-editable (INonEditableSensorsConfig) and only meaningful for some
    // sensor modes, so they may legitimately be missing. They're checked contextually
    // later in SensorsScreen instead (e.g. hall offsets gate isHallSensorSupported, and
    // resolver/encoder offsets are validated per-mode), so don't flag them here.
    // calibrationSpeed_elradps is likewise allowed to be NaN/default.
    const isCalibrationOffsetKey =
      k === 'hallsensorForwardOffset' ||
      k === 'hallsensorReverseOffset' ||
      k === 'resolverOffset' ||
      k === 'encoderOffset'
    const isOptionalKey = k === 'calibrationSpeed_elradps'

    if (!isFinite(v) && !isCalibrationOffsetKey && !isOptionalKey) {
      enqueueInvalid(new Error(`${k} was either not found in cstore, or has value ${v} in cstore`))
    }
  }
  if (!foundSensorMode) {
    enqueueInvalid(new Error(`sensorMode was not found in cstore`))
  }
  // Special case -- allow hallsensorDirection to be UNKNOWN
  return { ...result, invalidReasons }
}

export const getModificationsToSensorsConfig = (
  stored: SensorsConfig,
  newCfg: SensorsConfig
): Op[] => {
  let modifyOps: Op[] = []
  if (stored.sensorMode !== newCfg.sensorMode) {
    if (!isFinite(newCfg.sensorMode)) throw new Error('non-finite sensorMode')
    const tagName = cstoreFor('sensorsConfig.sensorMode')!
    modifyOps.push({ type: OpType.UpdateTag, id: tagName, value: newCfg.sensorMode })
  }
  if (stored.encoderCpr !== newCfg.encoderCpr) {
    if (!isFinite(newCfg.encoderCpr)) throw new Error('non-finite encoderCpr')
    const tagName = cstoreFor('sensorsConfig.encoderCpr')!
    modifyOps.push({ type: OpType.UpdateTag, id: tagName, value: newCfg.encoderCpr })
  }
  if (stored.accelerationRamp_mkradps2 !== newCfg.accelerationRamp_mkradps2) {
    if (!isFinite(newCfg.accelerationRamp_mkradps2))
      throw new Error('non-finite accelerationRamp_mkradps2')
    const tagName = cstoreFor('sensorsConfig.accelerationRamp_mkradps2')!
    modifyOps.push({ type: OpType.UpdateTag, id: tagName, value: newCfg.accelerationRamp_mkradps2 })
  }

  // Encoder/Resolver distinction
  if (newCfg.sensorMode === SensorMode.RESOLVER) {
    if ((stored.resolverOffset ?? NaN) !== (newCfg.resolverOffset ?? NaN)) {
      const val = newCfg.resolverOffset ?? NaN
      if (!isFinite(val)) {
        console.warn('non-finite resolverOffset')
      } else {
        const tagName = cstoreFor('sensorsConfig.resolverOffset')!
        modifyOps.push({ type: OpType.UpsertTag, id: tagName, value: val })
      }
    }
    if (
      (stored.resolverDirection ?? EncoderDirection.UNKNOWN) !==
      (newCfg.resolverDirection ?? EncoderDirection.UNKNOWN)
    ) {
      const val = newCfg.resolverDirection
      if (val === undefined || val === EncoderDirection.UNKNOWN) {
        console.warn('resolverDirection is UNKNOWN')
      } else {
        const tagName = cstoreFor('sensorsConfig.resolverDirection')!
        modifyOps.push({ type: OpType.UpsertTag, id: tagName, value: val })
      }
    }
  } else {
    if (stored.encoderOffset !== newCfg.encoderOffset) {
      if (!isFinite(newCfg.encoderOffset)) {
        console.warn('non-finite encoderOffset')
      } else {
        const tagName = cstoreFor('sensorsConfig.encoderOffset')!
        modifyOps.push({ type: OpType.UpdateTag, id: tagName, value: newCfg.encoderOffset })
      }
    }
    if (stored.encoderDirection !== newCfg.encoderDirection) {
      if (newCfg.encoderDirection === EncoderDirection.UNKNOWN) {
        console.warn('encoderDirection is UNKNOWN')
      } else {
        const tagName = cstoreFor('sensorsConfig.encoderDirection')!
        modifyOps.push({ type: OpType.UpdateTag, id: tagName, value: newCfg.encoderDirection })
      }
    }
  }

  if (
    stored.hallsensorForwardOffset !== newCfg.hallsensorForwardOffset &&
    isFinite(newCfg.hallsensorForwardOffset)
  ) {
    const tagName = cstoreFor('sensorsConfig.hallsensorForwardOffset')!
    modifyOps.push({ type: OpType.UpdateTag, id: tagName, value: newCfg.hallsensorForwardOffset })
  }
  if (
    stored.hallsensorReverseOffset !== newCfg.hallsensorReverseOffset &&
    isFinite(newCfg.hallsensorReverseOffset)
  ) {
    const tagName = cstoreFor('sensorsConfig.hallsensorReverseOffset')!
    modifyOps.push({ type: OpType.UpdateTag, id: tagName, value: newCfg.hallsensorReverseOffset })
  }
  if (
    stored.hallsensorDirection !== newCfg.hallsensorDirection &&
    newCfg.hallsensorDirection !== HallsensorDirection.UNKNOWN
  ) {
    const tagName = cstoreFor('sensorsConfig.hallsensorDirection')!
    modifyOps.push({ type: OpType.UpdateTag, id: tagName, value: newCfg.hallsensorDirection })
  }
  if (
    stored.calibrationSpeed_elradps !== newCfg.calibrationSpeed_elradps &&
    isFinite(newCfg.calibrationSpeed_elradps)
  ) {
    const tagName = cstoreFor('sensorsConfig.calibrationSpeed_elradps')!
    modifyOps.push({ type: OpType.UpdateTag, id: tagName, value: newCfg.calibrationSpeed_elradps })
  }
  return modifyOps
}

// Note: unlike the other "get...FromCStore" fns, this can return undefined if min and/or max are absent!
export const getPosCtrlCalResultsFromCStore = (cstore: CStore): PosCtrlCalResults | undefined => {
  let result: PosCtrlCalResults = {
    minPosition: NaN,
    maxPosition: NaN
  }
  let found = 0
  for (let i = 0; i < cstore.storedTags.length; i++) {
    const storedTag = cstore.storedTags[i]
    const mcName = mcPathFromStoredTag(storedTag, cstore.allTags)
    switch (mcName) {
      case 'positionControlCalib.minPosition':
        result.minPosition = roundFloat(storedTag.getData() as number)
        found++
        break
      case 'positionControlCalib.maxPosition':
        result.maxPosition = roundFloat(storedTag.getData() as number)
        found++
        break
    }
    // DS tags aren't in cstoreMapping — match by tag definition name directly
    try {
      const tagDef = storedTag.getTagDefinition(cstore.allTags)
      if (tagDef) {
        switch (tagDef.name) {
          case 'DS_ZeroPositionEncoder0':
            result.dareZeroEncoder0 = storedTag.getData() as number
            break
          case 'DS_ZeroPositionEncoder1':
            result.dareZeroEncoder1 = storedTag.getData() as number
            break
          case 'DS_CountDirection':
            result.dareCountDirection = storedTag.getData() as number
            break
        }
      }
    } catch {
      /* ignore corrupted DS tags */
    }
  }
  if (found !== 2) return
  return result
}

export const getModificationsToPosCtrlCalResults = (
  stored: PosCtrlCalResults | undefined,
  newCfg: PosCtrlCalResults
): Op[] => {
  let modifyOps: Op[] = []
  if (isFinite(newCfg.minPosition) && stored?.minPosition !== newCfg.minPosition) {
    const tagName = cstoreFor('positionControlCalib.minPosition')!
    modifyOps.push({ type: OpType.UpsertTag, id: tagName, value: newCfg.minPosition })
  }
  if (isFinite(newCfg.maxPosition) && stored?.maxPosition !== newCfg.maxPosition) {
    const tagName = cstoreFor('positionControlCalib.maxPosition')!
    modifyOps.push({ type: OpType.UpsertTag, id: tagName, value: newCfg.maxPosition })
  }
  if (
    isFinite(newCfg.dareZeroEncoder0 as number) &&
    stored?.dareZeroEncoder0 !== newCfg.dareZeroEncoder0
  ) {
    modifyOps.push({
      type: OpType.UpsertTag,
      id: 'DS_ZeroPositionEncoder0',
      value: newCfg.dareZeroEncoder0!
    })
  }
  if (
    isFinite(newCfg.dareZeroEncoder1 as number) &&
    stored?.dareZeroEncoder1 !== newCfg.dareZeroEncoder1
  ) {
    modifyOps.push({
      type: OpType.UpsertTag,
      id: 'DS_ZeroPositionEncoder1',
      value: newCfg.dareZeroEncoder1!
    })
  }
  if (
    isFinite(newCfg.dareCountDirection as number) &&
    stored?.dareCountDirection !== newCfg.dareCountDirection
  ) {
    modifyOps.push({
      type: OpType.UpsertTag,
      id: 'DS_CountDirection',
      value: newCfg.dareCountDirection!
    })
  }
  console.log(
    '[PosCtrlCal] modifyOps:',
    JSON.stringify(modifyOps),
    'from newCfg:',
    JSON.stringify(newCfg)
  )
  return modifyOps
}
